import React, { useEffect, useState } from 'react'

export default function IndicesGovernancePanel() {
  const [adminSessionReady, setAdminSessionReady] = useState(false)
  const [serie, setSerie] = useState('RIPTE')
  const [source, setSource] = useState('manual_admin')
  const [rowsText, setRowsText] = useState('')
  const [lotId, setLotId] = useState('')
  const [version, setVersion] = useState('')
  const [lotIdsText, setLotIdsText] = useState('')
  const [lots, setLots] = useState([])
  const [lotRows, setLotRows] = useState([])
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')

  const getBearerHeaders = () => {
    const token = localStorage.getItem('adminToken') || ''
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }

  const loadLots = async () => {
    try {
      const resp = await fetch('/api/normative/indices/staging/lots?limit=20')
      const data = await resp.json()
      setLots(Array.isArray(data?.data) ? data.data : [])
    } catch {
      setLots([])
    }
  }

  useEffect(() => {
    loadLots()
  }, [])

  const run = async (path, body) => {
    setMsg('')
    setError('')
    try {
      const resp = await fetch(path, { method: 'POST', headers: getBearerHeaders(), body: JSON.stringify(body || {}) })
      const data = await resp.json()
      if (!resp.ok || !data?.success) throw new Error(data?.error || 'Operacion rechazada')
      setMsg(data?.message || `OK ${path}`)
      await loadLots()
      return data
    } catch (e) {
      setError(e?.message || 'Error')
      return null
    }
  }

  const parseRows = () => rowsText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [periodo, valor] = line.split(',').map((x) => String(x || '').trim())
      return { periodo, valor: Number(valor) }
    })

  const createStagingLot = async () => {
    const rows = parseRows()
    const data = await run('/api/normative/indices/staging/upsert', { serie, source, rows })
    if (data?.lotId) setLotId(data.lotId)
  }

  const loadLot = async () => {
    setError('')
    try {
      const resp = await fetch(`/api/normative/indices/staging/${lotId}`)
      const data = await resp.json()
      if (!resp.ok || !data?.success) throw new Error(data?.error || 'No se pudo cargar lote')
      setLotRows(Array.isArray(data?.rows) ? data.rows : [])
    } catch (e) {
      setError(e?.message || 'Error cargando lote')
      setLotRows([])
    }
  }

  const approveAllLotRows = async () => {
    const decisions = lotRows.map((r) => ({ periodo: r.periodo, action: 'approve' }))
    await run(`/api/normative/indices/staging/${lotId}/review`, { decisions })
    await loadLot()
  }

  const publishVersion = async () => {
    const lotIds = lotIdsText.split('\n').map((x) => x.trim()).filter(Boolean)
    await run('/api/normative/indices/version/publish', { version, lotIds })
  }

  const activateVersion = async () => {
    await run(`/api/normative/indices/version/activate/${version}`, {})
  }

  const connectAdminSession = async () => {
    setMsg('')
    setError('')
    const token = localStorage.getItem('adminToken') || ''
    if (!token) {
      setAdminSessionReady(false)
      setError('No hay sesion admin activa. Inicia sesion desde Admin Login.')
      return
    }
    setAdminSessionReady(true)
    setMsg('Sesion admin activa validada.')
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
        <h3 className="text-sm font-semibold text-cyan-100">Gobernanza de Indices (Validacion Humana)</h3>
        <p className="mt-1 text-xs text-slate-300">Carga lote en staging, revisa, aprueba y publica version activable. Requiere sesion admin autenticada.</p>
        <button type="button" onClick={connectAdminSession} className="mt-2 rounded bg-cyan-700 px-3 py-2 text-xs font-semibold text-white hover:bg-cyan-600">Validar sesion activa</button>
        {!adminSessionReady && <p className="mt-2 text-[11px] text-amber-200">Sin validacion de sesion activa, las acciones admin pueden ser rechazadas.</p>}
        {msg && <p className="mt-2 text-xs text-emerald-700">{msg}</p>}
        {error && <p className="mt-2 text-xs text-rose-700">{error}</p>}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">1) Crear lote staging</h4>
          <div className="mt-2 space-y-2">
            <select value={serie} onChange={(e) => setSerie(e.target.value)} className="h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100">
              <option value="RIPTE">RIPTE</option>
              <option value="IPC">IPC</option>
              <option value="ISBIC">ISBIC</option>
            </select>
            <input value={source} onChange={(e) => setSource(e.target.value)} className="h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" placeholder="Fuente" />
            <textarea value={rowsText} onChange={(e) => setRowsText(e.target.value)} className="h-36 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100" placeholder="YYYY-MM,valor (una fila por linea)" />
            <button type="button" onClick={createStagingLot} className="rounded bg-cyan-700 px-3 py-2 text-xs font-semibold text-white">Crear lote</button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">2) Revisar lote</h4>
          <div className="mt-2 space-y-2">
            <input value={lotId} onChange={(e) => setLotId(e.target.value)} className="h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" placeholder="Lot ID" />
            <div className="flex gap-2">
              <button type="button" onClick={loadLot} className="rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-200">Cargar lote</button>
              <button type="button" onClick={approveAllLotRows} className="rounded border border-emerald-300 px-3 py-1.5 text-xs text-emerald-700">Aprobar todas</button>
            </div>
            <div className="max-h-44 overflow-auto rounded border border-slate-700 bg-slate-950 p-2 text-[11px] text-slate-300">
              {lotRows.map((r) => <p key={r.id}>- {r.periodo} | {r.valor} | {r.severity} | {r.status}</p>)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">3) Publicar version</h4>
          <input value={version} onChange={(e) => setVersion(e.target.value)} className="mt-2 h-9 w-full rounded border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" placeholder="Version indices (ej: idx-v1.0.0)" />
          <textarea value={lotIdsText} onChange={(e) => setLotIdsText(e.target.value)} className="mt-2 h-24 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100" placeholder="Lot IDs (uno por linea)" />
          <button type="button" onClick={publishVersion} className="mt-2 rounded bg-cyan-700 px-3 py-2 text-xs font-semibold text-white">Publicar version aprobada</button>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
          <h4 className="text-sm font-semibold text-cyan-100">4) Activar version</h4>
          <p className="mt-2 text-xs text-slate-300">Aplicara los indices aprobados a la coleccion operativa.</p>
          <button type="button" onClick={activateVersion} className="mt-3 rounded border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700">Activar version</button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow">
        <h4 className="text-sm font-semibold text-cyan-100">Lotes recientes</h4>
        <div className="mt-2 max-h-40 overflow-auto text-xs text-slate-300">
          {lots.map((l) => <p key={l.id}>- {l.id} | {l.serie} | {l.status} | ok:{l.stats?.ok || 0} warn:{l.stats?.warning || 0} crit:{l.stats?.critical || 0}</p>)}
        </div>
      </div>
    </div>
  )
}
